// <i80-pdf>: our own component (not in Helios), made for zPlan's collective-agreement editor.
// A calm PDF reader you can select text in: the PDF shows on a calm canvas, you select a
// passage, and the host binds it to a typed variable. Like <i80-flow> it only DRAWS and
// REPORTS — the server decides what a selection becomes and hands the bound passages back
// as <i80-pdf-mark> children, which the component draws read-only over the page.
//
// pdf.js is vendored and served next to i80.js (never a runtime CDN — zPlan runs on a closed
// network) and imported lazily on first use, so every other page stays small.
import { I80Element, define, h } from "../base.js";
import { icon } from "../icons.js";
import css from "./pdf.css";

const ACCENTS = { red: "500", amber: "600", blue: "500", purple: "500", green: "500" };
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

function ensureStyles() {
  if (document.querySelector("style[data-i80-pdf]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-i80-pdf", "");
  style.textContent = css;
  document.head.append(style);
}

// Load the vendored pdf.js once. `import.meta.url` is where i80.js is served from, so the
// library and its worker resolve to /<base>/pdf/ next to it.
let _pdfjs;
function pdfjsLib() {
  if (!_pdfjs) {
    const lib = new URL("pdf/pdf.js", import.meta.url).href;
    _pdfjs = import(lib).then((mod) => {
      mod.GlobalWorkerOptions.workerSrc = new URL("pdf/pdf.worker.js", import.meta.url).href;
      return mod;
    });
  }
  return _pdfjs;
}

/**
 * The text string of one page = each pdf.js text item's `str` in reading order, with a
 * newline after an item that ends a line. THIS is the contract: a mark's start/end are
 * character offsets into this string, and text === pageText(items).slice(start, end). The
 * server must build the same string from its own pdf.js getTextContent({disableNormalization:true}).
 */
function pageText(items) {
  let s = "";
  const bases = [];
  items.forEach((it, i) => {
    bases[i] = s.length;
    s += it.str;
    if (it.hasEOL) s += "\n";
  });
  return { string: s, bases };
}

/**
 * <i80-pdf src="/agreement/42.pdf" label="Overenskomst" height="600" accent-color="red">
 *   <i80-pdf-mark page="3" start="120" end="188" label="Hviletid"
 *     detail="§5 stk. 2 — 11 timers hvile" accent></i80-pdf-mark>
 * </i80-pdf>
 */
export class I80Pdf extends I80Element {
  static get observedAttributes() {
    return ["src", "label", "height", "accent-color", "page", "zoom", "label-loading", "label-error", "label-empty"];
  }

  get accent() {
    const fam = this.getAttribute("accent-color");
    return ACCENTS[fam] ? fam : "red";
  }

  get marks() {
    return [...this.querySelectorAll(":scope > i80-pdf-mark")];
  }

  setup() {
    ensureStyles();
    this.querySelector(':scope > [data-i80-part="pdf"]')?.remove();
    this._src = null;
    this._scale = clamp(parseFloat(this.getAttribute("zoom")) || 1.2, 0.4, 3);
    this._pages = new Map();
    this._current = 1;

    this._prev = this._iconButton("chevron-up", this.getAttribute("label-prev") ?? "Previous page");
    this._next = this._iconButton("chevron-down", this.getAttribute("label-next") ?? "Next page");
    this._info = h("span", { class: "i80-pdf__pageinfo" });
    this._out = this._iconButton("zoom-out", this.getAttribute("label-zoom-out") ?? "Zoom out");
    this._in = this._iconButton("zoom-in", this.getAttribute("label-zoom-in") ?? "Zoom in");
    this._prev.addEventListener("click", () => this._goToPage(this._current - 1));
    this._next.addEventListener("click", () => this._goToPage(this._current + 1));
    this._out.addEventListener("click", () => this._zoom(this._scale / 1.2));
    this._in.addEventListener("click", () => this._zoom(this._scale * 1.2));

    const toolbar = h("div", { class: "i80-pdf__toolbar", role: "group", "aria-label": this.getAttribute("label") ?? "PDF" },
      this._prev, this._next, this._info, h("span", { class: "i80-pdf__spacer" }), this._out, this._in);

    this._viewport = h("div", { class: "i80-pdf__viewport", tabindex: "0", role: "group", "aria-label": this.getAttribute("label") ?? "PDF" });
    this._root = h("div", { class: "i80-pdf", "data-i80-part": "pdf" }, toolbar, this._viewport);
    this.append(this._root);

    this._io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) this._renderPage(Number(e.target.dataset.page));
    }, { root: this._viewport, rootMargin: "400px 0px" });

    this._viewport.addEventListener("mouseup", () => this._onSelect());
    this._viewport.addEventListener("keyup", (e) => { if (e.shiftKey || e.key === "Shift") this._onSelect(); });
    this._viewport.addEventListener("scroll", () => this._trackCurrent(), { passive: true });

    // The marks (and only the marks) are the server's state, like flow's nodes. Our own
    // writes land inside _root, so we redraw only when a mark child changes.
    this._observer = new MutationObserver((records) => {
      const relevant = (r) => (r.target === this
        ? [...r.addedNodes, ...r.removedNodes].some((n) => n !== this._root)
        : !this._root.contains(r.target));
      if (records.some(relevant)) this._redrawAllMarks();
    });
    this._observer.observe(this, { childList: true, subtree: true, attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._observer?.disconnect();
    this._io?.disconnect();
    this._doc?.destroy?.();
  }

  update() {
    const fam = this.accent;
    this._root.style.setProperty("--i80-pdf-accent-tint", `var(--i80-color-palette-${fam}-100)`);
    this._root.style.setProperty("--i80-pdf-accent-ink", `var(--i80-color-palette-${fam}-${ACCENTS[fam]})`);
    this._root.style.setProperty("--i80-pdf-accent-edge", `var(--i80-color-palette-${fam}-300)`);
    this._viewport.style.height = `${parseInt(this.getAttribute("height") ?? "600", 10) || 600}px`;

    const src = this.getAttribute("src");
    if (src && src !== this._src) this._load(src);
    else this._redrawAllMarks();
  }

  _iconButton(name, label) {
    const btn = h("button", { type: "button", class: "i80-pdf__btn", "aria-label": label, title: label }, icon(name, { size: "16" }));
    return btn;
  }

  _updateToolbar() {
    this._info.textContent = this._numPages ? `${this._current} / ${this._numPages}` : "";
    this._prev.disabled = this._current <= 1;
    this._next.disabled = !this._numPages || this._current >= this._numPages;
  }

  // --- load and render ---

  async _load(src) {
    this._src = src;
    this._viewport.replaceChildren(h("div", { class: "i80-pdf__status" }, this.getAttribute("label-loading") ?? "Loading…"));
    let pdfjs;
    try {
      pdfjs = await pdfjsLib();
    } catch (e) {
      return this._fail(src, e);
    }
    if (this._src !== src) return;
    let doc;
    try {
      doc = await pdfjs.getDocument({ url: src }).promise;
    } catch (e) {
      return this._fail(src, e);
    }
    if (this._src !== src) return doc.destroy?.();

    this._doc?.destroy?.();
    this._doc = doc;
    this._numPages = doc.numPages;
    this._pages = new Map();
    this._current = clamp(parseInt(this.getAttribute("page"), 10) || 1, 1, this._numPages);
    this._viewport.replaceChildren();

    const first = await doc.getPage(1);
    const base = first.getViewport({ scale: this._scale });
    for (let n = 1; n <= this._numPages; n++) {
      const canvas = h("canvas", { class: "i80-pdf__canvas" });
      const textLayer = h("div", { class: "i80-pdf__text textLayer" });
      const marks = h("div", { class: "i80-pdf__marks" });
      const wrap = h("div", { class: "i80-pdf__page", "data-page": n, style: `width:${Math.round(base.width)}px;height:${Math.round(base.height)}px` }, canvas, textLayer, marks);
      wrap.style.setProperty("--total-scale-factor", this._scale);
      wrap.style.setProperty("--scale-round-x", "1px");
      wrap.style.setProperty("--scale-round-y", "1px");
      this._pages.set(n, { wrap, canvas, textLayer, marks, rendered: false, rendering: false, string: "", spans: [], bases: [] });
      this._viewport.append(wrap);
      this._io.observe(wrap);
    }
    this._updateToolbar();
    await this._renderPage(this._current);
    if (this._current > 1) this._pages.get(this._current)?.wrap.scrollIntoView();
  }

  _fail(src, e) {
    if (this._src !== src) return;
    console.warn("i80-pdf:", e?.message || e);
    this._viewport.replaceChildren(h("div", { class: "i80-pdf__status" }, this.getAttribute("label-error") ?? "The document could not be loaded."));
  }

  async _renderPage(n) {
    const p = this._pages.get(n);
    if (!p || p.rendered || p.rendering) return;
    p.rendering = true;
    try {
      const pdfjs = await pdfjsLib();
      const page = await this._doc.getPage(n);
      const vp = page.getViewport({ scale: this._scale });
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      p.wrap.style.width = `${Math.round(vp.width)}px`;
      p.wrap.style.height = `${Math.round(vp.height)}px`;
      p.canvas.width = Math.floor(vp.width * dpr);
      p.canvas.height = Math.floor(vp.height * dpr);
      p.canvas.style.width = `${Math.round(vp.width)}px`;
      p.canvas.style.height = `${Math.round(vp.height)}px`;
      await page.render({ canvasContext: p.canvas.getContext("2d"), viewport: vp, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null }).promise;

      const tc = await page.getTextContent({ disableNormalization: true });
      const items = tc.items.filter((it) => it.str !== undefined);
      const { string, bases } = pageText(items);
      p.textLayer.replaceChildren();
      const tl = new pdfjs.TextLayer({ textContentSource: tc, container: p.textLayer, viewport: vp });
      await tl.render();
      tl.textDivs.forEach((sp, i) => {
        sp.__base = bases[i];
        sp.__len = items[i].str.length;
        sp.setAttribute("data-i80-span", "");
      });
      p.spans = tl.textDivs;
      p.string = string;
      p.rendered = true;
      this._io.unobserve(p.wrap);
      this._drawMarks(n);
    } catch (e) {
      console.warn("i80-pdf page", n, e?.message || e);
    } finally {
      p.rendering = false;
    }
  }

  _goToPage(n) {
    n = clamp(n, 1, this._numPages || 1);
    const p = this._pages.get(n);
    if (!p) return;
    this._current = n;
    p.wrap.scrollIntoView({ behavior: "smooth", block: "start" });
    this._renderPage(n);
    this._updateToolbar();
  }

  _trackCurrent() {
    if (!this._numPages) return;
    const mid = this._viewport.scrollTop + this._viewport.clientHeight / 2;
    let best = this._current;
    for (const [n, p] of this._pages) {
      if (p.wrap.offsetTop <= mid && p.wrap.offsetTop + p.wrap.offsetHeight > mid) { best = n; break; }
    }
    if (best !== this._current) { this._current = best; this._updateToolbar(); }
  }

  async _zoom(scale) {
    scale = clamp(scale, 0.4, 3);
    if (Math.abs(scale - this._scale) < 0.001) return;
    this._scale = scale;
    for (const [, p] of this._pages) {
      p.rendered = false;
      p.rendering = false;
      p.textLayer.replaceChildren();
      p.marks.replaceChildren();
      p.wrap.style.setProperty("--total-scale-factor", scale);
      this._io.observe(p.wrap);
    }
    await this._renderPage(this._current);
  }

  // --- selection: report what the user picked ---

  _resolve(node, offset) {
    let span = null;
    let ch = 0;
    if (node.nodeType === Node.TEXT_NODE) {
      span = node.parentElement;
      ch = offset;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.hasAttribute?.("data-i80-span")) {
        span = node;
        ch = offset > 0 ? (node.textContent?.length ?? 0) : 0;
      } else {
        const child = node.childNodes[Math.min(offset, node.childNodes.length - 1)];
        if (child?.nodeType === Node.TEXT_NODE) {
          span = child.parentElement;
          ch = offset >= node.childNodes.length ? child.length : 0;
        } else if (child?.hasAttribute?.("data-i80-span")) {
          span = child;
        }
      }
    }
    if (!span?.hasAttribute?.("data-i80-span")) return null;
    const wrap = span.closest(".i80-pdf__page");
    if (!wrap) return null;
    return { page: Number(wrap.dataset.page), offset: span.__base + clamp(ch, 0, span.__len) };
  }

  _onSelect() {
    const sel = document.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!this._viewport.contains(range.commonAncestorContainer)) return;
    const a = this._resolve(range.startContainer, range.startOffset);
    const b = this._resolve(range.endContainer, range.endOffset);
    if (!a || !b || a.page !== b.page) return; // a selection is one passage on one page
    let start = a.offset;
    let end = b.offset;
    if (start > end) [start, end] = [end, start];
    if (start === end) return;
    const text = this._pages.get(a.page).string.slice(start, end);
    const r = range.getBoundingClientRect();
    this.dispatchEvent(new CustomEvent("i80-pdf-select", {
      bubbles: true,
      cancelable: true,
      detail: { page: a.page, start, end, text, rect: { x: r.x, y: r.y, width: r.width, height: r.height, top: r.top, right: r.right, bottom: r.bottom, left: r.left } },
    }));
  }

  // --- marks: draw the server's bound passages, read-only ---

  _redrawAllMarks() {
    if (!this._pages) return;
    for (const [n, p] of this._pages) if (p.rendered) this._drawMarks(n);
  }

  _markBoxes(p, start, end) {
    const wrapRect = p.wrap.getBoundingClientRect();
    const boxes = [];
    for (const sp of p.spans) {
      if (!sp.__len) continue;
      const s0 = sp.__base;
      const s1 = sp.__base + sp.__len;
      if (s1 <= start || s0 >= end) continue;
      const tn = sp.firstChild;
      if (!tn) continue;
      const r = document.createRange();
      r.setStart(tn, Math.max(start, s0) - s0);
      r.setEnd(tn, Math.min(end, s1) - s0);
      for (const rect of r.getClientRects()) {
        boxes.push({ left: rect.left - wrapRect.left, top: rect.top - wrapRect.top, width: rect.width, height: rect.height });
      }
    }
    return boxes;
  }

  _drawMarks(n) {
    const p = this._pages.get(n);
    if (!p?.rendered) return;
    p.marks.replaceChildren();
    for (const mark of this.marks) {
      if ((parseInt(mark.getAttribute("page"), 10) || 0) !== n) continue;
      const start = parseInt(mark.getAttribute("start"), 10);
      const end = parseInt(mark.getAttribute("end"), 10);
      if (!(end > start)) continue;
      const label = mark.getAttribute("label");
      const detail = mark.getAttribute("detail");
      const name = [label, detail].filter(Boolean).join(" — ") || "Marked passage";
      const boxes = this._markBoxes(p, start, end);
      boxes.forEach((box, i) => {
        const el = h("button", {
          type: "button",
          class: ["i80-pdf__mark", mark.hasAttribute("accent") && "i80-pdf__mark--accent"],
          style: `left:${box.left}px;top:${box.top}px;width:${box.width}px;height:${box.height}px`,
          "aria-label": i === 0 ? name : null,
          "aria-hidden": i === 0 ? null : "true",
          tabindex: i === 0 ? null : "-1",
          title: name,
        });
        el.addEventListener("click", () => {
          this.dispatchEvent(new CustomEvent("i80-pdf-mark-activate", { bubbles: true, cancelable: true, detail: { page: n, start, end, node: mark } }));
        });
        p.marks.append(el);
      });
    }
  }
}

define("i80-pdf", I80Pdf);
